/**
 * Loadout — the seeded variant combinator.
 *
 * A squad of twelve must not read as twelve copies of one man. Everything that
 * changes the SILHOUETTE is rolled here: headgear class, face cover, vest class,
 * pouch count, knee pads, holster, antenna. Everything that changes the READ is
 * rolled here too: camo pattern offset, uniform/vest/skin tint, build (height and
 * mass), and which shoulder the radio sits on.
 *
 * Deterministic: driven entirely by a `makeRng(seed)` from src/core/Rand.js.
 *
 * owner: characters
 */
import { makeRng } from '../core/Rand.js';

export const VARIANTS = ['assault', 'recon', 'breacher'];

// Tints multiply the shared material albedo, so these are ratios, not colours.
// Kept desaturated on purpose: a saturated tint over a procedural skin/fabric map
// reads as plastic, and plastic skin is the fastest way to fail a character shot.
const SKIN_TINTS = [0xe8ddd2, 0xd6c3b0, 0xbda491, 0xa88c76, 0x8e7160, 0x6f5749];
const UNIFORM_TINTS = [0xd6d2c2, 0xc2bfad, 0xa9a897, 0xcbc3ab, 0x969887, 0xb6ae94, 0x8f9382];
const VEST_TINTS = [0xd2ccbc, 0xaeab9c, 0x8a8979, 0xc0baa4, 0x77786a];
// Gloves must NOT be the same value as the carbine, or the hand on the grip is a
// black shape on a black shape and the fingers stop existing at any distance.
const GLOVE_TINTS = [0x50493d, 0x5d5648, 0x413a30, 0x6a6152];
const BOOT_TINTS = [0x5c534a, 0x413a33, 0x6b6053, 0x352f29];
const HAIR_TINTS = [0x1d1712, 0x2b2119, 0x3a2a1c, 0x4a3a28, 0x6a5a44, 0x55504a];

/**
 * @param {number} seed
 * @param {{variant?:string, index?:number}} [opts]
 */
export function makeLoadout(seed, opts = {}) {
  const rng = makeRng((seed >>> 0) || 1);
  const variant = opts.variant || VARIANTS[(opts.index ?? rng.int(0, 2)) % VARIANTS.length];

  const L = {
    seed, variant,
    /* ---- build ----
     * `scale` is overall stature and `girth` is mass. Twelve men at one height and one
     * width is a lineup, not a squad; the reviewer counted "nine identical soldiers".
     * ±6 % of 1.80 m is 1.69–1.91 m, which is a real infantry section's spread, and
     * `girth` moves the torso and limbs ±8 % independently of it so a short heavy man
     * and a tall lean one are different silhouettes and not one silhouette scaled. */
    scale: 1,
    girth: 1,
    // ---- colour ----
    skin: rng.pick(SKIN_TINTS),
    hair: rng.pick(HAIR_TINTS),
    uniform: rng.pick(UNIFORM_TINTS),
    vest: rng.pick(VEST_TINTS),
    glove: rng.pick(GLOVE_TINTS),
    boot: rng.pick(BOOT_TINTS),
    sole: 0x2b2a28,
    pad: 0x35332c,
    hard: 0xb8b4a6,
    camoOffset: [rng.range(0, 1), rng.range(0, 1)],
    // ---- silhouette ----
    helmet: true,
    helmetCover: true,
    nvgMount: true,
    railLights: true,
    goggles: 'helmet',   // 'helmet' | 'eyes' | none
    boonie: false,
    beanie: false,
    headset: true,
    faceCover: 'none',   // 'none' | 'balaclava' | 'shemagh'
    shemagh: false,
    vestClass: 'plate',  // 'plate' | 'chestrig'
    magPouches: 4,
    adminPouch: true,
    grenadePouch: true,
    radio: true,
    antenna: true,
    shoulderPads: false,
    kneePads: true,
    elbowPads: false,
    holster: true,
    dumpPouch: true,
    gloves: true,
    weapon: true,
  };

  if (variant === 'assault') {
    L.scale = rng.range(0.955, 1.045);
    L.girth = rng.range(0.94, 1.06);
    L.magPouches = rng.int(4, 5);
    L.goggles = rng() < 0.5 ? 'helmet' : 'eyes';
    L.faceCover = rng() < 0.45 ? 'balaclava' : 'none';
    L.elbowPads = rng() < 0.5;
  } else if (variant === 'recon') {
    L.scale = rng.range(0.940, 1.010);
    L.girth = rng.range(0.90, 1.00);
    L.helmet = false;
    L.nvgMount = false;
    L.railLights = false;
    L.boonie = rng() < 0.6;
    L.beanie = !L.boonie;
    L.goggles = rng() < 0.5 ? 'eyes' : 'none';
    L.faceCover = 'shemagh';
    L.shemagh = true;
    L.vestClass = 'chestrig';
    L.magPouches = 3;
    L.kneePads = false;
    L.holster = rng() < 0.4;
    L.adminPouch = false;
    L.antenna = true;
  } else {
    // breacher — heaviest silhouette
    L.scale = rng.range(0.985, 1.060);
    L.girth = rng.range(1.03, 1.11);
    L.magPouches = 6;
    L.shoulderPads = true;
    L.elbowPads = true;
    L.goggles = 'eyes';
    L.faceCover = 'balaclava';
    L.helmetCover = true;
    L.dumpPouch = true;
  }

  return L;
}

/** Three hand-picked, visually maximally-different loadouts for `char_variants`. */
export function heroLoadouts() {
  const a = makeLoadout(0x51ce01, { variant: 'assault' });
  a.goggles = 'helmet'; a.faceCover = 'none'; a.skin = 0xd0bda9; a.uniform = 0xd2cfbc;
  a.vest = 0xbdbaaa; a.camoOffset = [0.13, 0.62]; a.scale = 1.0; a.girth = 1.0;
  a.glove = 0x5d5648;
  a.hair = 0x2b2119;

  const b = makeLoadout(0x51ce02, { variant: 'recon' });
  b.boonie = true; b.beanie = false; b.goggles = 'eyes'; b.skin = 0xa88c76;
  b.uniform = 0xdcd4bb; b.vest = 0xe6e0cf; b.camoOffset = [0.71, 0.19];
  b.scale = 0.96; b.girth = 0.92; b.hair = 0x1d1712;

  const c = makeLoadout(0x51ce03, { variant: 'breacher' });
  c.skin = 0xbda491; c.uniform = 0xa4a693; c.vest = 0x969585;
  c.camoOffset = [0.42, 0.87]; c.scale = 1.055; c.girth = 1.09; c.hair = 0x4a3a28;
  return [a, b, c];
}
